import { useState, useRef, useLayoutEffect, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { fromJS } from "immutable";
import { useQuery } from "@tanstack/react-query";
import {
  BoxesIcon,
  Building,
  ChevronsUpDown,
  PencilIcon,
  Plus,
  Search,
  SquareKanbanIcon,
  Users,
  X,
} from "lucide-react";
import { Popover, PopoverButton, PopoverPanel } from "@headlessui/react";
import InputDebounce from "./InputDebounce";
import Avatar from "./Avatar";
import { search } from "../api/ProjectApi";
import { showCreateItemDialog } from "../Navigator";
import { useQuery as useQueryParams } from "../AppLayout";

const CreateItemButton = ({ contentType, reloadItem }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const currentParams = useQueryParams();
  const projectView = currentParams.get("view");

  const project = useSelector((state: any) => state.projectReducer.get("project"));

  return (
    <div
      className="mt-2 flex items-center gap-2 px-2 py-1 rounded-md hover:bg-gray-100 transition-colors cursor-pointer"
      onClick={() => {
        showCreateItemDialog({
          dispatch,
          type: contentType,
          item: {
            projectId: project.get("id"),
            type: contentType,
            projectView,
          },
          onClose: () => {
            reloadItem();
          },
          useInApp: false,
        });
      }}
    >
      <Plus className="w-4 h-4 text-gray-600" />
      <div className="text-sm font-medium text-gray-600">
        {contentType === "SALE"
          ? t("Create new deal")
          : contentType === "COMPANY"
            ? t("Create new account")
            : contentType === "CONTACT"
              ? t("Create new contact")
              : contentType === "PRODUCT"
                ? t("Create new product")
                : t("Create new")}
      </div>
    </div>
  );
};

interface ItemSelectProps {
  projectId: string;
  contentType: "SALE" | "COMPANY" | "CONTACT" | "PRODUCT";
  type: "INLINE" | "FORM" | "GRID" | "CUSTOM";
  value: any;
  onChange: (value: any) => void;
  placeholder?: string;
  isClearable?: boolean;
  showCreate?: boolean;
  popoverPanelWidth?: number;
  targetCustom?: React.ReactNode;
  testId?: string;
  onOpenItem?: (value: any) => void;
}

const ItemSelect = ({
  projectId,
  contentType,
  type,
  value,
  onChange,
  placeholder,
  isClearable,
  showCreate = true,
  popoverPanelWidth,
  targetCustom,
  testId,
  onOpenItem,
}: ItemSelectProps) => {
  const { t } = useTranslation();

  const popoverButtonRef = useRef(null);
  const popoverPanelRef = useRef(null);
  const [panelWidth, setPanelWidth] = useState(null);
  const [isOpen, setIsOpen] = useState(false);

  const [keywords, setKeywords] = useState("");

  const { data, isLoading, refetch } = useQuery({
    queryKey: [contentType, projectId, keywords],
    queryFn: async () => {
      let filters = {};
      if (keywords && keywords.trim()) {
        filters = {
          name: {
            operator: "regex",
            values: [keywords],
          },
        };
      }
      return await search({
        projectId: projectId,
        pageSize: 10,
        types: [contentType],
        filters,
      });
    },
    enabled: isOpen && !!contentType && !!projectId,
    staleTime: 60 * 1000, // cache 60s
  });

  const handleSelectItem = (item) => {
    const itemData: any = {
      label: item.getIn(["data", "name", "value"]),
      value: item.get("id"),
      description: "",
      id: item.get("id"),
      type: item.get("type"),
      iconUrl: item.get("iconUrl"),
      projectId: item.get("projectId"),
    };
    if (contentType === "SALE") {
    } else if (contentType === "COMPANY") {
      itemData.description = [
        item.getIn(["data", "street", "value"]),
        item.getIn(["data", "city", "value", "label"]),
        item.getIn(["data", "state", "value", "label"]),
        item.getIn(["data", "country", "value", "label"]),
      ]
        .filter(Boolean)
        .join(", ");
    } else if (contentType === "CONTACT") {
      itemData.description = item.getIn(["data", "email", "value"]);
    } else if (contentType === "PRODUCT") {
      itemData.unitPrice = item.getIn(["data", "unitPrice", "value"]);
      itemData.tax = item.getIn(["data", "tax", "value"]);
      itemData.productCode = item.getIn(["data", "productCode", "value"]);
      const imageIds = item.getIn(["data", "imageIds", "value"]) || fromJS([]);
      itemData.imageIds = imageIds.toJS();
    }
    onChange(itemData);
  };

  const renderSearch = () => {
    return (
      <div className="w-full flex items-center">
        <Search className="w-4 h-4 text-gray-400" />
        <InputDebounce
          autoFocus
          className={`w-full h-6 px-2 placeholder:text-gray-400 text-sm focus:outline-none`}
          placeholder={t("Search...")}
          value={keywords}
          onChange={(value: string) => {
            setKeywords(value);
          }}
          delay={600}
        />
      </div>
    );
  };

  const renderItemList = (onClose) => {
    if (isLoading) {
      return (
        <div
          className="flex items-center justify-center p-1 text-gray-400"
          data-testid={`item-select-${contentType.toLowerCase()}-for-item-loading`}
        >
          {t("Loading...")}
        </div>
      );
    }
    if (data?.pageResult?.content?.length === 0) {
      return (
        <div
          className="flex items-center justify-center p-1 text-gray-400"
          data-testid={`item-select-${contentType.toLowerCase()}-for-item-no-options`}
        >
          {t("No options")}
        </div>
      );
    }
    const items = fromJS(data?.pageResult?.content || []);
    return items.map((item, index) => {
      let description;
      let avatar;
      if (contentType === "SALE") {
        avatar = <SquareKanbanIcon className="w-6 h-6 text-gray-600" />;
      } else if (contentType === "COMPANY") {
        description = [
          item.getIn(["data", "street", "value"]),
          item.getIn(["data", "city", "value", "label"]),
          item.getIn(["data", "state", "value", "label"]),
          item.getIn(["data", "country", "value", "label"]),
        ]
          .filter(Boolean)
          .join(", ");
        avatar = (
          <Avatar
            avatarUrl={item.get("iconUrl")}
            displayName={item.getIn(["data", "name", "value"])}
          />
        );
      } else if (contentType === "CONTACT") {
        description = item.getIn(["data", "email", "value"]);
        avatar = (
          <Avatar
            avatarUrl={item.get("iconUrl")}
            displayName={item.getIn(["data", "name", "value"])}
          />
        );
      } else if (contentType === "PRODUCT") {
        avatar = <BoxesIcon className="w-6 h-6 text-gray-600" />;
      }
      return (
        <div
          key={item.get("id")}
          data-testid={`item-select-${contentType.toLowerCase()}-for-item-${index}`}
          className="flex items-center p-1 rounded-md hover:bg-gray-100 transition-colors gap-2 cursor-default"
          onClick={() => {
            handleSelectItem(item);
            onClose();
          }}
        >
          <div className="flex-none">{avatar}</div>
          <div className="flex flex-col flex-nowrap overflow-hidden">
            <div
              title={item.getIn(["data", "name", "value"])}
              className="text-sm font-medium leading-4 truncate"
            >
              {item.getIn(["data", "name", "value"])}
            </div>
            {description ? (
              <div
                title={description}
                className="text-xs font-medium leading-4 text-gray-600 truncate"
              >
                {description}
              </div>
            ) : null}
          </div>
        </div>
      );
    });
  };

  const renderTargetInline = () => {
    return (
      <div className="group w-full flex items-center justify-between rounded-md transition-colors duration-150 hover:bg-gray-50 flex-1 min-h-[28px] cursor-pointer">
        {value ? (
          <div className="shrink-0 flex items-center flex-nowrap gap-2 overflow-hidden cursor-default">
            {contentType === "SALE" ? (
              <SquareKanbanIcon className="w-5 h-5 text-gray-600" />
            ) : contentType === "COMPANY" ? (
              <Avatar avatarUrl={value.iconUrl} displayName={value.label} sizeIcon={5} />
            ) : contentType === "CONTACT" ? (
              <Avatar avatarUrl={value.iconUrl} displayName={value.label} sizeIcon={5} />
            ) : contentType === "PRODUCT" ? (
              <BoxesIcon className="w-5 h-5 text-gray-600" />
            ) : null}
            <div
              className={`flex items-center gap-1 ${onOpenItem ? "cursor-pointer hover:underline" : "cursor-default"}`}
              onClick={(e) => {
                if (onOpenItem) {
                  e.stopPropagation();
                  onOpenItem(value);
                }
              }}
            >
              <div className="text-xs font-medium leading-4">{value.label}</div>
            </div>
          </div>
        ) : (
          <div className="w-full text-left font-medium text-xs leading-4 text-gray-400">
            {placeholder}
          </div>
        )}
        <div className="ml-2 shrink-0 flex items-center flex-nowrap gap-4 opacity-0 group-hover:opacity-100 transition-opacity">
          {isClearable && !!value ? (
            <X
              className={`w-3.5 h-3.5 ${
                isOpen ? "text-gray-500 hover:text-gray-700" : "text-gray-300 hover:text-gray-400"
              }`}
              strokeWidth={4}
              onClick={() => {
                onChange(undefined);
              }}
            />
          ) : null}
          <PencilIcon className="w-3 h-3 text-gray-400 mr-2" />
        </div>
      </div>
    );
  };

  const renderTargetForm = () => {
    return (
      <div
        className={`w-full h-9.5 flex items-center justify-between overflow-hidden gap-2 px-3 cursor-default rounded-lg ${
          isOpen ? "ring-4 ring-blue-100 border border-blue-600" : "border border-gray-200"
        }`}
      >
        {value ? (
          <div className="flex items-center flex-nowrap overflow-hidden gap-2">
            {contentType === "SALE" ? (
              <SquareKanbanIcon className="w-6 h-6 text-gray-600" />
            ) : contentType === "COMPANY" ? (
              <Avatar avatarUrl={value.iconUrl} displayName={value.label} sizeIcon={6} />
            ) : contentType === "CONTACT" ? (
              <Avatar avatarUrl={value.iconUrl} displayName={value.label} sizeIcon={6} />
            ) : contentType === "PRODUCT" ? (
              <BoxesIcon className="w-6 h-6 text-gray-600" />
            ) : null}
            <div className="text-sm font-medium leading-4 truncate">{value.label}</div>
          </div>
        ) : (
          <div className="flex items-center flex-nowrap overflow-hidden gap-2">
            {contentType === "SALE" ? (
              <SquareKanbanIcon className="w-5 h-5 text-gray-500" />
            ) : contentType === "COMPANY" ? (
              <Building className="w-5 h-5 text-gray-500" />
            ) : contentType === "CONTACT" ? (
              <Users className="w-5 h-5 text-gray-500" />
            ) : contentType === "PRODUCT" ? (
              <BoxesIcon className="w-5 h-5 text-gray-500" />
            ) : null}
            <div className="text-sm leading-4 text-gray-500 truncate">{placeholder}</div>
          </div>
        )}
        <div className="shrink-0 flex items-center flex-nowrap gap-4">
          {isClearable && !!value ? (
            <X
              className={`w-3.5 h-3.5 ${
                isOpen ? "text-gray-500 hover:text-gray-700" : "text-gray-300 hover:text-gray-400"
              }`}
              strokeWidth={4}
              onClick={() => {
                onChange(undefined);
              }}
            />
          ) : null}
          <ChevronsUpDown className="w-3.5 h-3.5 text-gray-400" />
        </div>
      </div>
    );
  };

  const renderTargetGrid = () => {
    return (
      <div className="w-full cursor-pointer">
        {value ? (
          <div className="shrink-0 flex items-center flex-nowrap gap-2 overflow-hidden cursor-default">
            {contentType === "SALE" ? (
              <SquareKanbanIcon className="w-6 h-6 text-gray-600" />
            ) : contentType === "COMPANY" ? (
              <Avatar avatarUrl={value.iconUrl} displayName={value.label} sizeIcon={6} />
            ) : contentType === "CONTACT" ? (
              <Avatar avatarUrl={value.iconUrl} displayName={value.label} sizeIcon={6} />
            ) : contentType === "PRODUCT" ? (
              <BoxesIcon className="w-6 h-6 text-gray-600" />
            ) : null}
            <div className="text-sm font-medium leading-4">{value.label}</div>
          </div>
        ) : (
          <div className="w-full text-left leading-4 text-gray-500 cursor-default">
            {placeholder}
          </div>
        )}
      </div>
    );
  };

  let target;
  let popoverPanelClassName = "";
  if (type === "INLINE") {
    target = renderTargetInline();
  } else if (type === "FORM") {
    target = renderTargetForm();
    popoverPanelClassName = "mt-2";
  } else if (type === "GRID") {
    target = renderTargetGrid();
    popoverPanelClassName = "mt-1";
  } else if (type === "CUSTOM") {
    target = targetCustom;
    popoverPanelClassName = "mt-1";
  }

  return (
    <Popover>
      {({ open, close }) => {
        // eslint-disable-next-line react-hooks/rules-of-hooks
        useLayoutEffect(() => {
          if (!open) return;
          if (!popoverButtonRef.current || !popoverPanelRef.current) return;
          const buttonWidth = popoverButtonRef.current.offsetWidth;
          const panelNaturalWidth = popoverPanelRef.current.offsetWidth;
          const maxWidth = Math.max(buttonWidth, panelNaturalWidth);
          setPanelWidth(popoverPanelWidth || maxWidth);
        }, [open]);

        // eslint-disable-next-line react-hooks/rules-of-hooks
        useEffect(() => {
          setIsOpen(open);
        }, [open]);

        return (
          <>
            <PopoverButton
              data-testid={testId}
              ref={popoverButtonRef}
              as="div"
              onClick={(e) => {
                if (open) {
                  e.preventDefault();
                }
              }}
            >
              {target}
            </PopoverButton>
            <PopoverPanel
              ref={popoverPanelRef}
              anchor="bottom start"
              style={{ width: panelWidth ?? "auto" }}
              className={`z-9999 p-2 bg-white rounded-md border border-gray-300 shadow-xl ${popoverPanelClassName}`}
            >
              <div>
                {renderSearch()}
                <div className="w-full my-1 border-b border-gray-200"></div>
                <div className="w-full max-h-60 overflow-y-auto">{renderItemList(close)}</div>
                {showCreate && (
                  <>
                    <div className="w-full my-1 border-b border-gray-200"></div>
                    <CreateItemButton contentType={contentType} reloadItem={refetch} />
                  </>
                )}
              </div>
            </PopoverPanel>
          </>
        );
      }}
    </Popover>
  );
};

export default ItemSelect;
